import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { BreadcrumbsController } from './breadcrumbs.controller';

export type TruncateType = 'length' | 'depth';

/**
 * Hierarchical page-navigation trail that shows the user's current location
 * in the site structure.
 *
 * Orchestrates one or more `asc-breadcrumb-item` children, injects decorative
 * chevron separators in Shadow DOM, and optionally truncates deep paths with a
 * static, non-interactive `…` indicator. The last slotted item is automatically
 * marked as the active (current-page) item. Breadcrumb labels and structure are
 * provided via light DOM content, but when `asc-breadcrumb-item` renders links
 * via its `href` property those `<a>` elements live inside Shadow DOM, which
 * carries SEO crawlability tradeoffs as described in that component's documentation.
 *
 * @element asc-breadcrumbs
 *
 * @slot - Default slot. Accepts one or more `asc-breadcrumb-item` elements,
 *   ordered from root (home) to current page. Required.
 *
 * @csspart base          - The `<nav>` landmark element.
 * @csspart list          - The `<ol>` row container.
 * @csspart separator     - Each `<li>` wrapping a decorative chevron SVG between items.
 * @csspart ellipsis-item - The `<li>` wrapping the static ellipsis span.
 * @csspart ellipsis      - The non-interactive `<span>` displaying `…`.
 *
 * @cssprop --asc-breadcrumbs-gap - Gap between items and separators. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-breadcrumbs-separator-color - Chevron separator color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-breadcrumbs-separator-size - Chevron separator size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-breadcrumbs-ellipsis-color - Ellipsis text color. Default: `var(--asc-text-tertiary)`
 */
@customElement('asc-breadcrumbs')
export class AscBreadcrumbs extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    [part='list'] {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--asc-breadcrumbs-gap, var(--asc-spacing-xs));
      list-style: none;
      margin: 0;
      padding: 6px 0;
    }

    [part='separator'] {
      display: inline-flex;
      align-items: center;
      list-style: none;
      color: var(--asc-breadcrumbs-separator-color, var(--asc-fg-tertiary));
      font-size: var(--asc-breadcrumbs-separator-size, var(--asc-font-size-text-sm));
      /* Collapse gap contribution — the separator is the visual gap */
      margin-inline: calc(var(--asc-breadcrumbs-gap, var(--asc-spacing-xs)) * -0.5);
    }

    [part='ellipsis-item'] {
      display: inline-flex;
      align-items: center;
      list-style: none;
    }

    [part='ellipsis'] {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-breadcrumbs-separator-size, var(--asc-font-size-text-sm));
      color: var(--asc-breadcrumbs-ellipsis-color, var(--asc-text-tertiary));
      user-select: none;
    }
  `;

  private readonly _controller = new BreadcrumbsController(this);

  /**
   * The maximum number of non-active ancestor items to show before truncation
   * activates. When ancestor count ≤ `maxAncestors`, all items are displayed
   * with no ellipsis. Not reflected — no CSS attribute selector depends on it.
   */
  @property({ type: Number, attribute: 'max-ancestors' })
  maxAncestors = 2;

  /**
   * Controls which ancestors are hidden during truncation.
   * `'length'` — keeps the N closest ancestors to the current page (tail).
   * `'depth'`  — keeps the N ancestors closest to the root (head).
   * Reflected so `:host([truncate-type="depth"])` CSS selectors can apply
   * depth-specific overrides if needed.
   */
  @property({ type: String, reflect: true, attribute: 'truncate-type' })
  truncateType: TruncateType = 'length';

  private _handleSlotChange(e: Event): void {
    this._controller.onSlotChange(e);
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('truncateType') || changed.has('maxAncestors')) {
      this._controller.recompute();
    }
  }

  private _renderSeparator() {
    return html`
      <li aria-hidden="true" part="separator">
        <svg
          viewBox="0 0 16 16"
          width="1em"
          height="1em"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M6 3 L11 8 L6 13"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </li>
    `;
  }

  private _renderEllipsis() {
    return html`
      <li aria-hidden="true" part="ellipsis-item">
        <span part="ellipsis" aria-hidden="true">…</span>
      </li>
    `;
  }

  override render() {
    const { visibleItems, showEllipsis, ellipsisIndex } = this._controller;

    return html`
      <!-- Default slot — controller reads and re-slots items from here -->
      <slot @slotchange=${this._handleSlotChange}></slot>

      <nav part="base" aria-label="Breadcrumb">
        <ol part="list">
          ${visibleItems.map(
            (item, i) => html`
              ${i > 0 && !(showEllipsis && i === ellipsisIndex)
                ? this._renderSeparator()
                : nothing}
              ${showEllipsis && i === ellipsisIndex
                ? html`${this._renderSeparator()}${this._renderEllipsis()}${this._renderSeparator()}`
                : nothing}
              <li>
                <slot name=${`item-${item.dataset['slotIndex']}`}></slot>
              </li>
            `,
          )}
        </ol>
      </nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-breadcrumbs': AscBreadcrumbs;
  }
}
